import { Stack, Typography } from "@mui/material";
import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import InfoIcon from "../../../../assets/images/info.svg";
import {
  HomepageBannerButton,
  HomepageBannerCard,
  HomepageBannerContentWrapper,
  HomepageBannerDescription,
  HomepageBannerTitle,
  HomepageBannersWrapper,
  HomepageHeartBannerIcon,
  InfoTooltip,
} from "../../../../Styles/GlobalStyles";
import { Theme } from "../../../../Styles/GlobalTheme";

type BannerType = "default" | "info" | "warning" | "error";

declare global {
  interface Window {
    triggerOmniChat: any;
    minimizeChat: any;
  }
}

export interface BannerTextSegment {
  type: "text";
  text: string;
  id?: string;
}

export interface BannerLinkSegment {
  type: "link";
  text: string;
  id?: string;
  path?: string;
  url?: string;
}

export interface BannerTooltipSegment {
  type: "tooltip";
  text: string;
  id?: string;
  icon?: string;
}

export type BannerSegment = BannerTextSegment | BannerLinkSegment | BannerTooltipSegment;

export interface BannerIcon {
  src: string;
  id?: string;
}

export interface BannerContent {
  heading?: BannerSegment[];
  subheading?: BannerSegment[];
  icon?: BannerIcon;
}

interface HomepageNewBannersProps {
  content?: BannerContent;
  actionItem?: string;
  onActionHandle?: () => void;
  actionItemStyles?: React.CSSProperties;
  disabled?: boolean;
  bannerStyles?: React.CSSProperties;
  bannerType?: BannerType;
}

const bannerTextColors: Record<BannerType, string | undefined> = {
  default: undefined,
  info: undefined,
  warning: "#92400E",
  error: undefined,
};

const bannerBgColors: Record<BannerType, string | undefined> = {
  default: Theme.palette.customcolor.importantUpdatesBannerBg,
  info: `${Theme.palette.primary.main}0D`,
  warning: "#FFF8E6",
  error: "#FEF2F2",
};

export const HomepageNewBanners = ({
  content,
  actionItem,
  onActionHandle,
  actionItemStyles,
  disabled = false,
  bannerType = "default",
  bannerStyles = undefined,
}: HomepageNewBannersProps) => {
  const navigate = useNavigate();
  const textColor = bannerTextColors[bannerType];

  const withProtocol = (src?: string) => {
    if (!src) return undefined;
    return /^(https?:\/\/|\/|data:|blob:)/i.test(src) ? src : `https://${src}`;
  };

  const handleNavigate = (seg: BannerSegment) => {
    if (seg.id === "trigger-chat") {
      window.triggerOmniChat && window.triggerOmniChat("D582223DCFE61");
    } else if (seg.type === "link" && seg.path) {
      navigate(seg.path);
    }
  };

  const renderSegments = (segments?: BannerSegment[]): ReactNode => {
    if (!segments?.length) return null;
    return segments.map((seg, i) => {
      const key = seg.id ?? `seg-${i}`;
      if (seg.type === "link" && typeof seg.path === "string") {
        return (
          <Typography
            component="span"
            key={key}
            onClick={() => {
              handleNavigate(seg);
            }}
            sx={{
              color: Theme.palette.secondary.main,
              "&:hover": { cursor: "pointer" },
              fontWeight: "bold",
              fontSize: { xs: "12px", sm: "14px" },
            }}>
            {" "}
            {seg.text}{" "}
          </Typography>
        );
      }
      if (seg.type === "link") {
        const href = seg.url ?? "#";
        return (
          <a
            key={key}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: Theme.palette.secondary.main,
              textDecoration: "none",
              fontWeight: "bold",
            }}>
            {" "}
            {seg.text}{" "}
          </a>
        );
      }
      if (seg.type === "tooltip") {
        const tooltipIcon = withProtocol(seg.icon) ?? InfoIcon;
        return (
          <InfoTooltip
            key={key}
            title={<span>{seg.text}</span>}
            placement="top-start"
            slotProps={{
              tooltip: {
                sx: {
                  fontFamily: "DM Sans",
                  fontWeight: 400,
                  fontStyle: "normal",
                  fontSize: "16px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  whiteSpace: "nowrap",
                  maxWidth: "none",
                },
              },
            }}>
            <img
              src={tooltipIcon}
              alt="i"
              style={{
                verticalAlign: "middle",
                cursor: "pointer",
                marginLeft: "4px",
                width: "24px",
                height: "24px",
              }}
            />
          </InfoTooltip>
        );
      }
      if (seg.type === "text") {
        return <span key={key} dangerouslySetInnerHTML={{ __html: seg.text }} />;
      }
      return null;
    });
  };

  const resolvedIcon = withProtocol(content?.icon?.src);
  const resolvedTitle = renderSegments(content?.heading);
  const resolvedDescription = renderSegments(content?.subheading);
  return (
    <HomepageBannersWrapper sx={bannerStyles ? bannerStyles : undefined}>
      <HomepageBannerCard
        sx={{
          backgroundColor: bannerBgColors[bannerType],
        }}>
        <Stack flexDirection={"row"} alignItems={"center"} gap={2} flexGrow={1}>
          {resolvedIcon && <HomepageHeartBannerIcon src={resolvedIcon} alt="banner-icon" />}
          <HomepageBannerContentWrapper spacing={0.5}>
            {resolvedTitle && (
              <HomepageBannerTitle sx={textColor ? { color: textColor } : undefined}>
                {resolvedTitle}
              </HomepageBannerTitle>
            )}
            {resolvedDescription && (
              <HomepageBannerDescription sx={textColor ? { color: textColor } : undefined}>
                {resolvedDescription}
              </HomepageBannerDescription>
            )}
          </HomepageBannerContentWrapper>
        </Stack>
        {actionItem && (
          <HomepageBannerButton
            disabled={disabled}
            onClick={onActionHandle ?? (() => {})}
            style={actionItemStyles}>
            {actionItem}
          </HomepageBannerButton>
        )}
      </HomepageBannerCard>
    </HomepageBannersWrapper>
  );
};
